import { useEffect, useRef, useState } from 'react';
import { useDesktopAuth, type DesktopOrg } from './desktop-auth';
import { openWebExternal } from './electron-open-web';

// Custom org switcher for the Electron desktop session (no Clerk in the renderer).
// Mirrors Clerk's prebuilt OrganizationSwitcher: logo trigger + dropdown listing
// orgs (logo, name, role, active check). Switches via the backend desktop session;
// create/manage delegate to the web.
export function ElectronOrganizationSwitcher() {
  const { orgId, orgs, switchOrg } = useDesktopAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const active = orgs.find((o) => o.id === orgId);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  function close() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  async function switchTo(id: string) {
    if (id === orgId) { close(); return; }
    setBusy(true);
    try {
      await switchOrg(id);
      close();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={active ? `Organización ${active.name}` : 'Cambiar organización'}
        aria-haspopup="menu"
        aria-expanded={open}
        title={active?.name ?? 'Organización'}
        className="w-10 h-10 rounded-xl bg-accent/10 text-accent font-semibold flex items-center justify-center overflow-hidden ring-1 ring-stroke hover:ring-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-all"
      >
        <OrgAvatar org={active} className="w-full h-full text-sm" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            role="menu"
            aria-label="Cambiar de organización"
            className="absolute left-full ml-2 top-0 z-50 w-64 rounded-2xl border border-stroke-hi bg-surface-card shadow-xl p-1.5"
          >
            <p className="px-2.5 py-1.5 text-[11px] font-medium uppercase tracking-wide text-ink-muted">
              Organizaciones
            </p>

            {orgs.length === 0 && (
              <p className="px-2.5 py-2 text-sm text-ink-muted">Sin organizaciones</p>
            )}

            {orgs.map((o) => {
              const isActive = o.id === orgId;
              return (
                <button
                  key={o.id}
                  role="menuitem"
                  disabled={busy}
                  onClick={() => switchTo(o.id)}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left hover:bg-surface-hover focus-visible:outline-none focus-visible:bg-surface-hover disabled:opacity-60 transition-colors"
                >
                  <OrgAvatar org={o} className="w-8 h-8 text-xs flex-none" />
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm truncate leading-tight ${isActive ? 'text-accent font-medium' : 'text-ink'}`}>
                      {o.name}
                    </p>
                    {isActive && o.role && (
                      <p className="text-xs text-ink-soft truncate mt-0.5">{roleLabel(o.role)}</p>
                    )}
                  </div>
                  {isActive && <CheckIcon />}
                </button>
              );
            })}

            <div className="my-1 border-t border-stroke" />

            <button
              role="menuitem"
              onClick={() => { openWebExternal(); close(); }}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-sm text-ink-muted hover:bg-surface-hover focus-visible:outline-none focus-visible:bg-surface-hover transition-colors"
            >
              <PlusIcon />
              Gestionar / crear organización
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function OrgAvatar({ org, className = '' }: { org?: DesktopOrg; className?: string }) {
  const initial = (org?.name ?? '?').charAt(0).toUpperCase();
  if (org?.imageUrl) {
    return (
      <img src={org.imageUrl} alt="" className={`rounded-md object-cover ${className}`} />
    );
  }
  return (
    <span className={`rounded-md bg-accent/10 text-accent font-semibold flex items-center justify-center ${className}`} aria-hidden="true">
      {initial}
    </span>
  );
}

function roleLabel(role: string): string {
  const r = role.toLowerCase();
  if (r.includes('admin')) return 'Administrador';
  if (r.includes('member') || r.includes('basic')) return 'Miembro';
  return role;
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-none text-accent" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-none" aria-hidden="true">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
